import votingLight from "@/assets/images/event-type-voting-light.webp";
import votingDark from "@/assets/images/event-type-voting-dark.webp";
import concertLight from "@/assets/images/event-type-concert-light.webp";
import concertDark from "@/assets/images/event-type-concert-dark.webp";
import karaokeLight from "@/assets/images/event-type-karaoke-light.webp";
import karaokeDark from "@/assets/images/event-type-karaoke-dark.webp";

export const EVENT_TYPES = {
  PREVOTING: "prevoting",
  CONCERT: "concert",
  KARAOKE: "karaoke"
};

export const EVENT_TYPE_OPTIONS = [
  {
    value: EVENT_TYPES.PREVOTING,
    label: "Pre-voting",
    description: "Open voting ahead of the show to plan the first set or full playlist.",
    images: { light: votingLight, dark: votingDark },
    selectable: true
  },
  {
    value: EVENT_TYPES.CONCERT,
    label: "Concert",
    description: "Full live concert control with voting, setlists, and performance tracking.",
    images: { light: concertLight, dark: concertDark },
    selectable: true
  },
  {
    value: EVENT_TYPES.KARAOKE,
    label: "Karaoke",
    description: "Queue songs with performer names; staff controls start and end times.",
    images: { light: karaokeLight, dark: karaokeDark },
    selectable: true
  }
];

export function getEventTypeLabel(eventType) {
  const normalized = normalizeEventType(eventType, null);
  if (!normalized) return "Concert";
  const option = EVENT_TYPE_OPTIONS.find((item) => item.value === normalized);
  return option?.label || "Concert";
}

export function resolveEventType(event) {
  if (!event) return EVENT_TYPES.CONCERT;
  if (event.eventType != null && String(event.eventType).trim() !== "") {
    return normalizeEventType(event.eventType);
  }
  return EVENT_TYPES.CONCERT;
}

export function getEventTypeLabelForEvent(event) {
  return getEventTypeLabel(resolveEventType(event));
}

export function getEventTypeShortCode(eventType) {
  switch (normalizeEventType(eventType, null)) {
    case EVENT_TYPES.PREVOTING:
      return "V";
    case EVENT_TYPES.KARAOKE:
      return "K";
    default:
      return "C";
  }
}

export function getEventTypeShortCodeForEvent(event) {
  return getEventTypeShortCode(resolveEventType(event));
}

export function getEventTypeImage(eventType, isDark) {
  const option = EVENT_TYPE_OPTIONS.find((item) => item.value === eventType);
  if (!option) return isDark ? concertDark : concertLight;
  return isDark ? option.images.dark : option.images.light;
}

export function normalizeEventType(eventType, fallback = EVENT_TYPES.CONCERT) {
  if (!eventType) return fallback;
  const value = String(eventType).trim().toLowerCase();
  if (value === "prevoting" || value === "pre-voting" || value === "prevote") {
    return EVENT_TYPES.PREVOTING;
  }
  if (Object.values(EVENT_TYPES).includes(value)) {
    return value;
  }
  return fallback;
}

export function isPrevoting(eventType) {
  return normalizeEventType(eventType) === EVENT_TYPES.PREVOTING;
}

/**
 * Detect pre-voting events from persisted eventType.
 */
export function isPrevotingEvent(event) {
  return resolveEventType(event) === EVENT_TYPES.PREVOTING;
}

export function isKaraoke(eventType) {
  return normalizeEventType(eventType) === EVENT_TYPES.KARAOKE;
}

export function isKaraokeEvent(event) {
  return resolveEventType(event) === EVENT_TYPES.KARAOKE;
}

export function isSetlistAllowed(eventType) {
  return normalizeEventType(eventType) === EVENT_TYPES.CONCERT;
}

export function getEventTypeBadgeClass(eventType) {
  switch (eventType) {
    case EVENT_TYPES.PREVOTING:
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
    case EVENT_TYPES.KARAOKE:
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200";
    default:
      return "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200";
  }
}
